import React, { useState } from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Container, Row, Col } from 'react-bootstrap';

const FAQ: React.FC = () => {
  const isMobile = useIsMobile();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is NayaBajar?",
          a: "NayaBajar is Nepal's online classifieds platform where you can buy and sell a wide variety of products including mobile phones, vehicles, electronics, furniture, and much more. It's completely free to post ads!"
        },
        {
          q: "Is it free to use NayaBajar?",
          a: "Yes! Posting basic ads on NayaBajar is completely free. We also offer paid boosting/featured options to give your ad more visibility."
        },
        {
          q: "How do I create an account?",
          a: "Click on 'Sign Up' button, enter your email, phone number, and create a password. You'll need to verify your email and phone number to activate your account."
        },
        {
          q: "Do I need an account to browse ads?",
          a: "No, you can browse all ads without an account. However, you'll need an account to post ads, save favorites, and contact sellers."
        }
      ]
    },
    {
      category: "Posting Ads",
      questions: [
        {
          q: "How do I post an ad?",
          a: "After logging in, click 'Post Ad' button. Fill in the ad details including title, description, price, category, and upload photos. Review and submit. Your ad will be live after moderation."
        },
        {
          q: "How many ads can I post for free?",
          a: "Free accounts can post up to 5 active ads at a time. To post more, you can upgrade to a premium plan or delete/expiring old ads."
        },
        {
          q: "What can I sell on NayaBajar?",
          a: "You can sell almost anything legal! This includes electronics, vehicles, property, furniture, fashion items, services, and more. Check our Posting Rules for prohibited items."
        },
        {
          q: "How many photos can I add?",
          a: "You can add up to 10 photos per ad. We recommend adding clear, well-lit photos from multiple angles to attract more buyers."
        },
        {
          q: "Can I edit or delete my ad?",
          a: "Yes! Go to 'My Ads' in your dashboard, select the ad, and you can edit details, mark as sold, or delete it anytime."
        },
        {
          q: "How long does my ad stay active?",
          a: "Ads stay active for 30 days. You'll receive a notification before expiration. You can renew or repost your ad after it expires."
        }
      ]
    },
    {
      category: "Buying & Selling",
      questions: [
        {
          q: "How do I contact a seller?",
          a: "Click 'Contact Seller' or the phone number button on any ad listing. You can call, message, or use the 'Chat' feature if available."
        },
        {
          q: "Is NayaBajar involved in the transaction?",
          a: "No, NayaBajar is only a platform to connect buyers and sellers. We don't handle payments, shipping, or provide buyer protection. All transactions are directly between buyers and sellers."
        },
        {
          q: "How do I know if a seller is genuine?",
          a: "Check if the seller is verified (blue tick badge). Look at their profile, other ads, and ratings. Always meet in person, inspect the item, and follow our Safety Tips."
        },
        {
          q: "Can I negotiate the price?",
          a: "Yes! Most prices are negotiable. Contact the seller to discuss. We recommend being polite and reasonable when negotiating."
        },
        {
          q: "What if I have a problem with a seller?",
          a: "If you encounter fraud or suspicious activity, report it immediately to support@nayabajar.com. For serious issues, also file a complaint with local authorities."
        }
      ]
    },
    {
      category: "Payments & Boosting",
      questions: [
        {
          q: "How do I boost/feature my ad?",
          a: "Go to 'My Ads', select the ad you want to boost, and choose a boosting option (Featured, Trending, etc.). Payment can be made via eSewa, Khalti, or bank transfer."
        },
        {
          q: "What are the benefits of boosting?",
          a: "Boosted ads appear at the top of search results, get highlighted in the homepage, and receive up to 10x more views. Featured ads also get a special badge."
        },
        {
          q: "How much does boosting cost?",
          a: "Prices start from NPR 99 for basic boosting. Featured ads cost NPR 499 for 7 days. Check our Pricing page for detailed packages."
        },
        {
          q: "Can I get a refund?",
          a: "Refunds are processed on a case-by-case basis. Contact support within 24 hours if you've been charged incorrectly. No refunds for ads removed due to policy violations."
        }
      ]
    },
    {
      category: "Account & Settings",
      questions: [
        {
          q: "How do I reset my password?",
          a: "Click 'Forgot Password' on the login page. Enter your email, and we'll send a reset link. Follow the instructions to create a new password."
        },
        {
          q: "Can I change my username?",
          a: "Usernames cannot be changed once set. If you really need to change it, you'll need to create a new account and contact us to transfer your ads."
        },
        {
          q: "How do I verify my account?",
          a: "Verification requires confirming your email and phone number. For business accounts, you may need to submit business documents. Verified accounts get a blue tick badge."
        },
        {
          q: "Can I close my account?",
          a: "Yes, go to Settings > Account > Close Account. All your ads will be deleted. This action cannot be undone."
        },
        {
          q: "How do I report a problem?",
          a: "Use the 'Contact Us' page or email support@nayabajar.com. Describe your issue in detail, and we'll respond within 24 hours."
        }
      ]
    },
    {
      category: "Safety & Security",
      questions: [
        {
          q: "Is NayaBajar safe?",
          a: "We take safety seriously with verification, moderation, and reporting tools. However, always follow our Safety Tips: meet in public, inspect items, and trust your instincts."
        },
        {
          q: "What should I do if I suspect fraud?",
          a: "Stop communication immediately, do not make any payments, report the user to us, and if necessary, file a police report. Save all messages and evidence."
        },
        {
          q: "Are my messages private?",
          a: "Yes, messages are private between buyers and sellers. However, we may review messages for fraud prevention or if required by law."
        }
      ]
    }
  ];

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const content = (
    <Container className="py-4 px-3" style={{ maxWidth: '896px' }}>
      <div className="bg-white rounded-3 shadow p-4 p-md-5">
        <h1 className="fw-bold text-dark mb-2" style={{ fontSize: '30px' }}>Frequently Asked Questions</h1>
        <p className="text-secondary mb-4">
          Find answers to common questions about using NayaBajar
        </p>

        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-5">
            <h2 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2" style={{ fontSize: '20px' }}>
              <span className="rounded" style={{ width: '8px', height: '32px', background: '#0d6efd' }}></span>
              {category.category}
            </h2>

            <div className="d-flex flex-column gap-2">
              {category.questions.map((faq, questionIndex) => {
                const index = categoryIndex * 100 + questionIndex;
                const isOpen = openIndex === index;

                return (
                  <div
                    key={questionIndex}
                    className="border rounded overflow-hidden"
                  >
                    <button
                      onClick={() => toggleQuestion(index)}
                      className="w-100 d-flex align-items-center justify-content-between p-3 text-start bg-white text-decoration-none"
                      style={{ transition: 'background-color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      <span className="fw-medium text-dark pe-3">{faq.q}</span>
                      {isOpen ? (
                        <ChevronUpIcon className="text-secondary flex-shrink-0" style={{ width: '20px', height: '20px' }} />
                      ) : (
                        <ChevronDownIcon className="text-secondary flex-shrink-0" style={{ width: '20px', height: '20px' }} />
                      )}
                    </button>
                    {isOpen && (
                      <div className="p-3 pb-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <p className="text-secondary">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact Section */}
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #e5e7eb' }}>
          <div className="rounded-3 p-4 text-center" style={{ backgroundColor: 'rgba(13, 110, 253, 0.05)' }}>
            <h3 className="fw-semibold text-dark mb-2">
              Still have questions?
            </h3>
            <p className="text-secondary mb-3">
              Can't find what you're looking for? We're here to help!
            </p>
            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
              <a
                href="/contact-us"
                className="d-inline-block text-white rounded-3 fw-medium text-decoration-none"
                style={{ padding: '10px 24px', backgroundColor: '#0d6efd' }}
              >
                Contact Us
              </a>
              <a
                href="mailto:support@nayabajar.com"
                className="d-inline-block border rounded-3 fw-medium text-decoration-none text-primary"
                style={{ padding: '10px 24px', borderColor: '#0d6efd' }}
              >
                Email Support
              </a>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-4">
          <Row className="g-2">
            <Col xs={6} md={4}>
              <a href="/safety-tips" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Safety Tips
              </a>
            </Col>
            <Col xs={6} md={4}>
              <a href="/posting-rules" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Posting Rules
              </a>
            </Col>
            <Col xs={6} md={4}>
              <a href="/terms" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Terms of Service
              </a>
            </Col>
            <Col xs={6} md={4}>
              <a href="/privacy" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Privacy Policy
              </a>
            </Col>
            <Col xs={6} md={4}>
              <a href="/pricing" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Pricing
              </a>
            </Col>
            <Col xs={6} md={4}>
              <a href="/contact-us" className="text-primary text-decoration-none" style={{ fontSize: '14px' }}>
                → Contact Us
              </a>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );

  return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false}>{content}</Layout>;
};

export default FAQ;
